import React, { useState } from 'react';
import {
  SiteOverageFee,
  SiteOverageFeeCreate,
  SiteOverageFeeUpdate,
  StoreOverageFee,
  OverageFeeType,
  FeeFrequency,
  OVERAGE_FEE_TYPE_LABELS,
  FEE_FREQUENCY_LABELS
} from '../../../types/site';
import { siteService } from '../../../services/siteService';
import { formatCurrency } from '../../../utils/formatters';
import EffectiveSummary, { EffectiveItem, computeEffectiveItems } from './EffectiveSummary';

// ============================================================================
// SiteOverageChargesForm - Dedicated form for overage charges (occupancy-based)
// UI Label: "Overage Charges" | DB Tables: store_overage_fees, site_overage_fees
// Hierarchy: Site overrides Store for matching fee_type
// ============================================================================

interface SiteOverageChargesFormProps {
  siteId: number;
  storeId: number;
  overageFees: SiteOverageFee[];
  storeOverageFees: StoreOverageFee[];
  onChange: (fees: SiteOverageFee[]) => void;
}

interface OverageChargeFormData {
  fee_type: OverageFeeType;
  base_included: number;
  max_allowed: number;
  amount: number;
  flat: boolean;
  frequency: FeeFrequency;
  active: boolean;
}

const emptyFormData: OverageChargeFormData = {
  fee_type: 'adult',
  base_included: 2,
  max_allowed: 6,
  amount: 10,
  flat: true,
  frequency: 'nightly',
  active: true
};

const SiteOverageChargesForm: React.FC<SiteOverageChargesFormProps> = ({
  siteId,
  storeId,
  overageFees,
  storeOverageFees,
  onChange
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<OverageChargeFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compute effective overage charges (site overrides store for matching fee_type)
  const effectiveCharges = computeEffectiveItems<StoreOverageFee, SiteOverageFee, OverageFeeType>(
    storeOverageFees.filter(f => f.active),
    overageFees.filter(f => f.active),
    (s) => s.fee_type,
    (s) => s.fee_type
  );

  // Get fee types that already have site overrides
  const siteOverrideFeeTypes = new Set(overageFees.map(f => f.fee_type));

  // Handle copy from store to site
  const handleCopyToSite = async (storeFee: StoreOverageFee) => {
    // Check if site already has this fee type
    if (siteOverrideFeeTypes.has(storeFee.fee_type)) {
      setError(`Site already has a ${OVERAGE_FEE_TYPE_LABELS[storeFee.fee_type]} charge configured.`);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const createData: SiteOverageFeeCreate = {
        store_id: storeId,
        site_id: siteId,
        fee_category_id: storeFee.fee_category_id!,
        fee_type: storeFee.fee_type,
        base_included: storeFee.base_included,
        max_allowed: storeFee.max_allowed,
        amount: storeFee.amount,
        flat: storeFee.flat,
        frequency: storeFee.frequency,
        active: true
      };

      const created = await siteService.createSiteOverageFee(createData);
      onChange([...overageFees, created]);
    } catch (err) {
      setError('Failed to copy charge to site');
      console.error('Error copying to site:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = () => {
    // Find a fee type that doesn't have a site override yet
    const availableFeeTypes: OverageFeeType[] = ['adult', 'child', 'pet', 'vehicle'];
    const availableType = availableFeeTypes.find(t => !siteOverrideFeeTypes.has(t));

    if (availableType) {
      // Pre-populate from store default if exists
      const storeDefault = storeOverageFees.find(f => f.fee_type === availableType && f.active);
      if (storeDefault) {
        setFormData({
          fee_type: availableType,
          base_included: storeDefault.base_included,
          max_allowed: storeDefault.max_allowed,
          amount: storeDefault.amount,
          flat: storeDefault.flat,
          frequency: storeDefault.frequency,
          active: true
        });
      } else {
        setFormData({ ...emptyFormData, fee_type: availableType });
      }
    } else {
      setFormData(emptyFormData);
    }

    setEditingId(null);
    setShowAddForm(true);
    setError(null);
  };

  const handleEdit = (fee: SiteOverageFee) => {
    setFormData({
      fee_type: fee.fee_type,
      base_included: fee.base_included,
      max_allowed: fee.max_allowed,
      amount: fee.amount,
      flat: fee.flat,
      frequency: fee.frequency,
      active: fee.active
    });
    setEditingId(fee.id);
    setShowAddForm(true);
    setError(null);
  };

  const handleCancel = () => {
    setFormData(emptyFormData);
    setEditingId(null);
    setShowAddForm(false);
    setError(null);
  };

  const handleSave = async () => {
    if (formData.max_allowed < formData.base_included) {
      setError('Max allowed must be greater than or equal to base included');
      return;
    }

    if (formData.amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    // Check for duplicate fee_type on create
    if (!editingId && siteOverrideFeeTypes.has(formData.fee_type)) {
      setError(`Site already has a ${OVERAGE_FEE_TYPE_LABELS[formData.fee_type]} charge configured.`);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingId) {
        // Update existing
        const updateData: SiteOverageFeeUpdate = {
          base_included: formData.base_included,
          max_allowed: formData.max_allowed,
          amount: formData.amount,
          flat: formData.flat,
          frequency: formData.frequency,
          active: formData.active
        };
        const updated = await siteService.updateSiteOverageFee(editingId, updateData);
        onChange(overageFees.map(f => f.id === editingId ? updated : f));
      } else {
        // Create new - get fee_category_id from matching store overage fee
        const matchingStoreFee = storeOverageFees.find(f => f.fee_type === formData.fee_type);
        const createData: SiteOverageFeeCreate = {
          store_id: storeId,
          site_id: siteId,
          fee_category_id: matchingStoreFee?.fee_category_id!,
          fee_type: formData.fee_type,
          base_included: formData.base_included,
          max_allowed: formData.max_allowed,
          amount: formData.amount,
          flat: formData.flat,
          frequency: formData.frequency,
          active: formData.active
        };
        const created = await siteService.createSiteOverageFee(createData);
        onChange([...overageFees, created]);
      }

      handleCancel();
    } catch (err) {
      setError('Failed to save overage charge');
      console.error('Error saving overage charge:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (feeId: number) => {
    if (!window.confirm('Delete this site-specific overage charge? The store default will apply instead.')) {
      return;
    }

    try {
      await siteService.deleteSiteOverageFee(feeId);
      onChange(overageFees.filter(f => f.id !== feeId));
    } catch (err) {
      console.error('Error deleting overage charge:', err);
    }
  };

  // Render overage charge info
  const renderChargeInfo = (fee: StoreOverageFee | SiteOverageFee) => {
    const amountStr = fee.flat ? formatCurrency(fee.amount) : `${fee.amount}%`;
    return (
      <>
        <span className="charge-type">{OVERAGE_FEE_TYPE_LABELS[fee.fee_type]}</span>
        <span className="charge-details">
          {fee.base_included} included, max {fee.max_allowed} |{' '}
          {amountStr} {FEE_FREQUENCY_LABELS[fee.frequency]}
        </span>
      </>
    );
  };

  return (
    <div className="site-overage-charges-form">
      {/* Error Display */}
      {error && (
        <div className="form-error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Store Default Overage Charges Section */}
      <div className="hierarchy-section store-defaults-section">
        <div className="hierarchy-section-header">
          <h4 className="hierarchy-section-title">Store Default Overage Charges</h4>
          <span className="source-badge store">Store Default</span>
        </div>
        <p className="hierarchy-section-description">
          These are the default occupancy charges. Create site-specific overrides below to customize for this site.
        </p>

        {storeOverageFees.filter(f => f.active).length === 0 ? (
          <p className="hierarchy-empty-message">
            No store-level overage charges configured.
          </p>
        ) : (
          <div className="hierarchy-items-list">
            {storeOverageFees
              .filter(f => f.active)
              .map(fee => (
                <div key={fee.id} className="hierarchy-item store-default-item">
                  <div className="hierarchy-item-content">
                    {renderChargeInfo(fee)}
                  </div>
                  {!siteOverrideFeeTypes.has(fee.fee_type) && (
                    <button
                      type="button"
                      className="btn-copy-to-site"
                      onClick={() => handleCopyToSite(fee)}
                      disabled={saving}
                      title="Copy to site-specific override"
                    >
                      Copy to Site
                    </button>
                  )}
                  {siteOverrideFeeTypes.has(fee.fee_type) && (
                    <span className="overridden-badge">Overridden</span>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Site-Specific Overage Charges Section */}
      <div className="hierarchy-section site-overrides-section">
        <div className="hierarchy-section-header">
          <h4 className="hierarchy-section-title">Site-Specific Overage Charges</h4>
          <span className="source-badge site">Site Specific</span>
        </div>
        <p className="hierarchy-section-description">
          These charges override the store defaults for this site.
        </p>

        {overageFees.length === 0 ? (
          <p className="hierarchy-empty-message">
            No site-specific overage charges. Store defaults will apply.
          </p>
        ) : (
          <div className="hierarchy-items-list">
            {overageFees.map(fee => (
              <div
                key={fee.id}
                className={`hierarchy-item site-override-item ${!fee.active ? 'inactive' : ''}`}
              >
                <div className="hierarchy-item-content">
                  {renderChargeInfo(fee)}
                  {!fee.active && <span className="inactive-badge">Inactive</span>}
                </div>
                <div className="hierarchy-item-actions">
                  <button
                    type="button"
                    className="btn btn-text btn-sm"
                    onClick={() => handleEdit(fee)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-text btn-sm btn-danger"
                    onClick={() => handleDelete(fee.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="charge-form-card">
            <h4>{editingId ? 'Edit Overage Charge' : 'Add Site-Specific Overage Charge'}</h4>

            <div className="form-row">
              <div className="form-group">
                <label>Charge Type <span className="required">*</span></label>
                <select
                  value={formData.fee_type}
                  onChange={(e) => setFormData({ ...formData, fee_type: e.target.value as OverageFeeType })}
                  disabled={editingId !== null}
                >
                  {Object.entries(OVERAGE_FEE_TYPE_LABELS).map(([key, label]) => (
                    <option
                      key={key}
                      value={key}
                      disabled={!editingId && siteOverrideFeeTypes.has(key as OverageFeeType)}
                    >
                      {label}
                      {!editingId && siteOverrideFeeTypes.has(key as OverageFeeType) ? ' (already exists)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Base Included <span className="required">*</span></label>
                <input
                  type="number"
                  value={formData.base_included}
                  onChange={(e) => setFormData({ ...formData, base_included: Number(e.target.value) })}
                  min="0"
                />
                <span className="form-hint">Number included before charges apply</span>
              </div>
              <div className="form-group">
                <label>Max Allowed <span className="required">*</span></label>
                <input
                  type="number"
                  value={formData.max_allowed}
                  onChange={(e) => setFormData({ ...formData, max_allowed: Number(e.target.value) })}
                  min="0"
                />
                <span className="form-hint">Maximum limit</span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Charge Amount <span className="required">*</span></label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Amount Type</label>
                <select
                  value={formData.flat ? 'flat' : 'percent'}
                  onChange={(e) => setFormData({ ...formData, flat: e.target.value === 'flat' })}
                >
                  <option value="flat">Flat Amount ($)</option>
                  <option value="percent">Percentage (%)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Frequency</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as FeeFrequency })}
                >
                  {Object.entries(FEE_FREQUENCY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="toggle-row">
              <div className="toggle-label">
                <span>Active</span>
                <span>Enable this overage charge</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="form-actions">
              <button className="btn btn-secondary" onClick={handleCancel} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        )}

        {!showAddForm && (
          <button
            type="button"
            className="btn btn-primary btn-sm hierarchy-add-btn"
            onClick={handleAddNew}
          >
            + Add Site-Specific Overage Charge
          </button>
        )}
      </div>

      {/* Effective Overage Charges Summary */}
      <EffectiveSummary
        title="Effective Overage Charges"
        description="These charges will apply to reservations at this site."
        items={effectiveCharges as EffectiveItem<StoreOverageFee | SiteOverageFee>[]}
        renderItem={(item, source) => (
          <div className="effective-charge-item">
            {renderChargeInfo(item)}
          </div>
        )}
        emptyMessage="No overage charges configured."
      />
    </div>
  );
};

export default SiteOverageChargesForm;
